// src/modules/study-chat/useStudyChat.ts
import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase as defaultClient } from '@/lib/supabase';
import type { Message } from './types';
import { studyChatApi } from './api';

type UseStudyChatOpts = {
  client?: typeof defaultClient;
  pageSize?: number; // default 200
};

export function useStudyChat(groupId: number, opts: UseStudyChatOpts = {}) {
  const sb = opts.client ?? defaultClient;
  const pageSize = opts.pageSize ?? 200;
  const api = useMemo(() => studyChatApi({ client: sb }), [sb]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Used to force re-subscribe after a clear
  const [streamVersion, setStreamVersion] = useState(0);

  // Keep track of what we've already added to avoid dupes from initial fetch + realtime inserts
  const seenIdsRef = useRef<Set<string>>(new Set());

  // Optional: track current cleared_at (if your API returns it) to ignore stale inserts
  const clearedAtRef = useRef<string | null>(null);

  // Helper to reset local state (used on clear and when switching groups)
  const resetLocal = () => {
    setMessages([]);
    seenIdsRef.current.clear();
  };

  useEffect(() => {
    let channel: ReturnType<typeof sb.channel> | null = null;
    let cancelled = false;

    const run = async () => {
      try {
        setError(null);
        setLoading(true);

        // 1) initial fetch
        const initial = await api.fetchMessages(groupId, pageSize);
        if (cancelled) return;

        resetLocal();
        // Seed seen set and state in one pass
        for (const m of initial) seenIdsRef.current.add(String(m.id));
        setMessages(initial);

        // If your API can expose cleared_at, store it (optional)
        try {
          const groupMeta = await api.fetchGroupMeta?.(groupId); // optional API
          if (groupMeta?.cleared_at) clearedAtRef.current = groupMeta.cleared_at;
        } catch {
          // silently ignore if not implemented
        }

        // 2) realtime subscribe (INSERT on messages, UPDATE on group)
        channel = sb
          .channel(`study_group:${groupId}:${streamVersion}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'study_group_messages',
              filter: `group_id=eq.${groupId}`,
            },
            (payload: any) => {
              const msg = payload.new as Message;
              const id = String(msg.id);

              // ignore duplicates
              if (seenIdsRef.current.has(id)) return;

              // ignore stale inserts older than cleared_at (if known)
              if (clearedAtRef.current && msg.created_at < clearedAtRef.current) return;

              seenIdsRef.current.add(id);
              setMessages((prev) => [...prev, msg]);
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'study_groups',
              filter: `id=eq.${groupId}`,
            },
            async (payload: any) => {
              // If cleared_at changed, refetch
              const newClearedAt: string | undefined = payload?.new?.cleared_at;
              if (newClearedAt) clearedAtRef.current = newClearedAt;
              const refreshed = await api.fetchMessages(groupId, pageSize);
              if (cancelled) return;

              resetLocal();
              for (const m of refreshed) seenIdsRef.current.add(String(m.id));
              setMessages(refreshed);
            }
          )
          .subscribe();
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? 'Failed to load messages');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (channel) {
        try {
          sb.removeChannel(channel);
        } catch {
          /* noop */
        }
      }
    };
    // re-run when the group changes or when we bump streamVersion (e.g., after clear)
  }, [sb, api, groupId, pageSize, streamVersion]);

  const post = async (body: string) => {
    const msg = await api.postMessage(groupId, body);
    // merge locally for snappy UX; realtime will also deliver it but we de-dupe with seenIds
    if (msg?.id != null) {
      const id = String(msg.id);
      if (!seenIdsRef.current.has(id)) {
        seenIdsRef.current.add(id);
        setMessages((prev) => [...prev, msg]);
      }
    }
    return msg;
  };

  const clear = async () => {
    await api.clearMessages(groupId);
    // Optimistic local wipe so the user sees immediate feedback
    resetLocal();
    // Force a resubscribe to ensure we don’t get any replay from an old channel
    setStreamVersion((v) => v + 1);
  };

  return {
    messages,
    loading,
    error,
    post,
    clear,
  };
}
